import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard, SectionLabel } from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

const MENU_LABELS = {
  "new-arrivals": ["New Arrivals", "The most recent runs to leave the bench."],
  "best-sellers": ["Best Sellers", "The pieces our owners come back for."],
  men: ["Men", "Built for pockets, briefcases and long Mondays."],
  women: ["Women", "Structured bags and small goods, softly finished."],
  bags: ["Bags", "Totes, briefcases, messengers and weekenders."],
  watches: ["Watches", "Swiss automatics on straps cut in our own atelier."],
  "watches-men": ["Watches for Men", "Automatics and chronographs on hand-stitched leather straps."],
  "watches-women": ["Watches for Women", "Slim cases in rose gold and polished gold, sized for a narrow wrist."],
  perfume: ["Perfume", "Small-batch eau de parfum built around leather, amber and rose."],
  "perfume-men": ["Perfume for Men", "Smoked leather, tobacco and vetiver — our house accords for evening."],
  "perfume-women": ["Perfume for Women", "Rose over suede, iris and white musk. Floral, never sweet."],
  "small-leather-goods": ["Small Leather Goods", "Wallets, card holders, belts and folios."],
  shoes: ["Shoes", "Goodyear-welted and resoleable for a decade."],
  "leather-clothing": ["Leather Clothing", "Jackets and overshirts in glove-soft hides."],
  travel: ["Travel", "Duffels, folios and kits made for the road."],
  business: ["Business", "Quiet pieces for meetings that matter."],
  gifts: ["Gifts", "Objects worth unwrapping, and keeping."],
  collections: ["Collections", "Everything currently in the atelier."],
};

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const menu = params.get("menu") || "";
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState("featured");

  useEffect(() => { setTerm(q); }, [q]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/products", { params: { category, menu: menu || undefined, q: q || undefined } })
      .then((r) => setProducts(r.data)).catch(() => {});
  }, [category, menu, q]);

  const sorted = [...products].sort((a, b) =>
    sort === "low" ? a.price - b.price : sort === "high" ? b.price - a.price : Number(b.featured) - Number(a.featured));

  const [title, blurb] = q
    ? [`Results for “${q}”`, `${products.length} piece${products.length === 1 ? "" : "s"} matched your search.`]
    : MENU_LABELS[menu] || (category === "all"
      ? ["Everything we make", "Small runs, full-grain hides, no seasonal churn. Each piece is finished by a single maker."]
      : [category, "Small runs, full-grain hides, no seasonal churn."]);

  const setParam = (patch) => {
    const next = {};
    if (menu) next.menu = menu;
    if (q) next.q = q;
    if (category !== "all") next.category = category;
    Object.entries(patch).forEach(([k, v]) => { if (v) next[k] = v; else delete next[k]; });
    setParams(next);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="catalog-page">
      <SectionLabel>{q ? "Search" : menu ? MENU_LABELS[menu]?.[0] : "The Collection"}</SectionLabel>
      <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none" data-testid="catalog-title">
        {title}
      </h1>
      <p className="mt-6 text-luxa-espresso/60 max-w-xl">{blurb}</p>

      <form onSubmit={(e) => { e.preventDefault(); setParam({ q: term.trim(), category: null }); }}
        className="mt-10 flex gap-3 max-w-md" data-testid="catalog-search-form">
        <div className="relative flex-1">
          <Search size={15} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-luxa-espresso/40" />
          <Input data-testid="catalog-search-input" value={term} onChange={(e) => setTerm(e.target.value)}
            placeholder="Search the collection" className="rounded-none bg-luxa-shell border-luxa-sand h-12 pl-11" />
        </div>
        {q && (
          <button type="button" data-testid="clear-search" onClick={() => { setTerm(""); setParams(menu ? { menu } : {}); }}
            className="px-4 border border-luxa-sand overline hover:border-luxa-espresso flex items-center gap-2">
            <X size={13} strokeWidth={1.5} /> Clear
          </button>
        )}
      </form>

      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-b border-luxa-sand pb-5">
        <div className="flex flex-wrap gap-x-7 gap-y-3">
          {["all", ...cats].map((c) => (
            <button key={c} data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setParam({ category: c === "all" ? null : c })}
              className={`overline pb-1 border-b transition-colors duration-300 ${category === c ? "text-luxa-brown border-luxa-brown" : "text-luxa-espresso/50 border-transparent hover:text-luxa-espresso"}`}>
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
          className="ml-auto bg-transparent overline text-luxa-espresso/60 focus:outline-none cursor-pointer">
          <option value="featured">Sort: Featured</option>
          <option value="low">Price: Low to High</option>
          <option value="high">Price: High to Low</option>
        </select>
      </div>

      <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-x-10 gap-y-16">
        {sorted.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
      {sorted.length === 0 && <p className="mt-16 text-luxa-espresso/50" data-testid="catalog-empty">Nothing matched. Try another search or browse the full collection.</p>}
    </div>
  );
}
